import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Divider, Drawer, Stack, Typography } from "@mui/material";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { closeBranchDrawer, openBranchDrawer, setActiveBranch } from "@/store/uiSlice";
import { useFetchBranchQuery } from "@/features/organizations/api/branchApi";
import { Branch, Schedule } from "@/types/branch";
import { isAvailableToday } from "@/utils/weekDays";
import { setBranch } from "@/features/order/slice";
import EmptyState from "./EmptyState";
import BranchCard from "./BranchCard";

const SelectBranchDrawer = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();

  const organizationId = useAppSelector((state) => state.ui.activeOrganization);
  const isDrawerOpen = useAppSelector((state) => state.ui.isBranchDrawerOpen);
  const activeBranchId = useAppSelector((state) => state.ui.activeBranch);

  const { data: branches = [] } = useFetchBranchQuery(organizationId!);

  const handleSelect = (branch: Branch) => {
    dispatch(setActiveBranch(branch.id));
    dispatch(setBranch({
      id: branch.id,
      title: branch.title,
      location: branch.location,
      whatsapp: branch.whatsapp,
    }));
    localStorage.setItem("branchId", branch.id);
    dispatch(closeBranchDrawer());
  };

  const activeBranches = useMemo(() => {
    const availableBranches = branches.filter((branch) => {
      const available = isAvailableToday(branch?.schedule as unknown as Schedule);
      return available;
    });
    return availableBranches;
  }, [branches]);

  const isMenuRelatedPage = useMemo(() => {
    const path = location.pathname;
    const segments = path.split('/').filter(Boolean);
    
    // Allowed on main organization page (1 segment) or root (0 segments)
    if (segments.length <= 1) return true;
    
    const allowedSegments = ['menus', 'catalogo', 'cotizacion'];
    return segments.some(segment => allowedSegments.includes(segment));
  }, [location.pathname]);

  useEffect(() => {
    if (!branches.length) return;

    if (branches.length === 1) {
      const onlyBranch = branches[0];

      if (activeBranchId !== onlyBranch.id) {
        dispatch(setActiveBranch(onlyBranch.id));
        localStorage.setItem("branchId", onlyBranch.id);
        dispatch(setBranch({
          id: onlyBranch.id,
          title: onlyBranch.title,
          location: onlyBranch.location,
          whatsapp: onlyBranch.whatsapp,
        }));
      }

      return;
    }
    if (!activeBranchId && isMenuRelatedPage) {
      dispatch(openBranchDrawer());
    }
  }, [branches, activeBranchId, dispatch, isMenuRelatedPage]);

  if (!branches.length || branches.length === 1 || !isMenuRelatedPage) return null;

  return (
    <Drawer anchor="bottom"
      open={isDrawerOpen}
      onClose={() => {
        if (!activeBranchId) return;
        dispatch(closeBranchDrawer());
      }}
      ModalProps={{
        disableEscapeKeyDown: !activeBranchId,
      }}
    >
      <Stack px={2} py={3}>
        <Typography variant="h4">Sucursales</Typography>
      </Stack>

      <Divider />

      <Stack px={2} py={2} pb={4} flex={1} spacing={2} sx={{ backgroundColor: '#f5f5f5' }}>
        {activeBranches.length > 0 ? activeBranches.map((branch) => (
          <BranchCard
            key={branch.id}
            branch={branch}
            onClick={handleSelect}
            isActive={activeBranchId === branch.id}
          />
        )) : (
          <EmptyState
            title="Sin sucursal"
            description="No hay sucursales disponibles"
          />
        )
        }
      </Stack>
    </Drawer>
  );
};


export default SelectBranchDrawer;